import React from "react";
import { Container } from "@cloudscape-design/components";
import { SpaceBetween } from "@cloudscape-design/components";
import Popover from "@cloudscape-design/components/popover";
import Button from "@cloudscape-design/components/button";
import Header from "@cloudscape-design/components/header";
import { CategoryIcon, ImpactIcon, TargetIcon } from "./CustomButton.jsx";
import Badge from "@cloudscape-design/components/badge";
import Grid from "@cloudscape-design/components/grid";
import TokenGroup from "@cloudscape-design/components/token-group";
import ButtonDropdown from "@cloudscape-design/components/button-dropdown";
import ExpandableSection from "@cloudscape-design/components/expandable-section";
import Box from "@cloudscape-design/components/box";
import { ModalComponent } from "./ModalForm.jsx";
import ToggleButton from "@cloudscape-design/components/toggle-button";
import AttackTreeButton from "./AttackTreeButton.jsx";

export const ThreatComponent = React.memo((props) => {
  const [items, setItems] = React.useState([]);
  const isReadOnly = props?.isReadOnly || false;
  
  // Parse mitigations and convert URLs to clickable links
  React.useEffect(() => {
    const parsedItems = props?.data?.mitigations?.map((str) => {
      // Check for markdown link pattern [text](url)
      const markdownLinkRegex = /\[([^\]]+)\]\(([^\)]+)\)/g;
      const markdownMatches = [...str.matchAll(markdownLinkRegex)];
      
      if (markdownMatches.length > 0) {
        // Has markdown links - render as JSX with clickable links
        const parts = [];
        let lastIndex = 0;
        
        markdownMatches.forEach((match) => {
          const [fullMatch, text, url] = match;
          const matchIndex = match.index;
          
          // Add text before link
          if (matchIndex > lastIndex) {
            parts.push(str.substring(lastIndex, matchIndex));
          }
          
          // Add clickable link
          parts.push(
            <a 
              key={`link-${matchIndex}`}
              href={url} 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ 
                color: '#0073bb', 
                textDecoration: 'underline',
                cursor: 'pointer',
                pointerEvents: 'auto'
              }}
            >
              {text}
            </a>
          );
          
          lastIndex = matchIndex + fullMatch.length;
        });
        
        // Add remaining text
        if (lastIndex < str.length) {
          parts.push(str.substring(lastIndex));
        }
        
        return { label: <>{parts}</> };
      }
      
      // Check for plain URLs in (Source: https://...) or (⚠️ External: https://...) format
      // Also handles format with optional description: (⚠️ External: description https://...)
      const plainUrlRegex = /\((Source|⚠️\s*External):\s*([^)]*?)(https?:\/\/[^\s)]+)\)/g;
      const plainMatches = [...str.matchAll(plainUrlRegex)];
      
      if (plainMatches.length > 0) {
        // Has plain URLs - convert to clickable links
        const parts = [];
        let lastIndex = 0;
        
        plainMatches.forEach((match) => {
          const [fullMatch, prefix, description, url] = match;
          const matchIndex = match.index;
          
          // Add text before link
          if (matchIndex > lastIndex) {
            parts.push(str.substring(lastIndex, matchIndex));
          }
          
          // Add clickable link with original prefix format
          parts.push(
            <span key={`source-${matchIndex}`}>
              ({prefix}: {description}<a 
                href={url} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ 
                  color: '#0073bb', 
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  pointerEvents: 'auto'
                }}
              >
                {url}
              </a>)
            </span>
          );
          
          lastIndex = matchIndex + fullMatch.length;
        });
        
        // Add remaining text
        if (lastIndex < str.length) {
          parts.push(str.substring(lastIndex));
        }
        
        return { label: <>{parts}</> };
      }
      
      // No links - return plain text (handles internal sources like tool-catalog.json)
      return { label: str };
    });
    
    setItems(parsedItems || []);
  }, [props?.data?.mitigations]);

  const [openModal, setOpenModal] = React.useState(false);
  const [action, setAction] = React.useState(null);
  const handleModal = () => {
    setOpenModal(true);
  };

  return (
    <>
      <Container
        header={
          <Header
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <AttackTreeButton
                  threatId={props?.data?.id || `threat-${props?.index}`}
                  threatName={props?.data?.name}
                  onOpenAttackTree={props?.onOpenAttackTree}
                  disabled={!props?.onOpenAttackTree}
                />
                {!isReadOnly && (
                  <ButtonDropdown
                    onItemClick={(itemClickDetails) => {
                      setAction(itemClickDetails.detail.id);
                      if (itemClickDetails.detail.id === "edit") {
                        handleModal();
                      }
                      if (itemClickDetails.detail.id === "delete") {
                        props?.updateData(props?.type, props?.index, null);
                      }
                    }}
                    items={[
                      { id: "edit", text: "Edit", disabled: false },
                      { id: "delete", text: "Delete", disabled: false },
                    ]}
                    ariaLabel={`Edit ${props?.data?.name} Threat`}
                    variant="icon"
                  />
                )}
              </SpaceBetween>
            }
          >
            <SpaceBetween direction="horizontal" size="m">
              <SpaceBetween direction="horizontal" size="xs">
                <div style={{ marginTop: "-3px" }}>
                  <ToggleButton
                    onChange={({ detail }) => {
                      if (!isReadOnly) {
                        props?.updateData(props?.type, props?.index, {
                          ...props?.data,
                          starred: detail.pressed,
                        });
                      }
                    }}
                    pressed={props?.data?.starred}
                    ariaLabel="Favorite"
                    iconName="star"
                    pressedIconName="star-filled"
                    variant="icon"
                    disabled={isReadOnly}
                  />
                </div>
                <div>{props?.data?.name}</div>
                <Badge color={(() => {
                  const riskScore = props?.data?.risk_score || 'Medium';
                  // 5 colors for 5 risk score levels
                  if (riskScore === 'High') return 'red';
                  if (riskScore === 'Medium/High') return 'severity-high';
                  if (riskScore === 'Medium') return 'severity-medium';
                  if (riskScore === 'Low/Medium') return 'green';
                  if (riskScore === 'Low') return 'grey';
                  return 'severity-medium';
                })()}>
                  {props?.data?.risk_score || 'Medium'}
                </Badge>
                <Badge color={(() => {
                  const riskScore = props?.data?.risk_score || 'Medium';
                  // Color-code priorities by urgency
                  if (riskScore === 'High') return 'red';           // P0 - Critical
                  if (riskScore === 'Medium/High') return 'severity-high';  // P1 - High
                  if (riskScore === 'Medium') return 'blue';         // P2 - Medium
                  if (riskScore === 'Low/Medium') return 'green';    // P3 - Low
                  if (riskScore === 'Low') return 'grey';            // P4 - Minimal
                  return 'blue';
                })()}>
                  {(() => {
                    const riskScore = props?.data?.risk_score || 'Medium';
                    // Map risk scores to priority levels
                    const priorityMap = {
                      'High': 'P0',
                      'Medium/High': 'P1',
                      'Medium': 'P2',
                      'Low/Medium': 'P3',
                      'Low': 'P4'
                    };
                    return priorityMap[riskScore] || 'P2';
                  })()}
                </Badge>
                {props?.data?.status && (
                  <Badge color={(() => {
                    const status = props?.data?.status;
                    if (status === 'Done') return 'green';
                    if (status === 'To do') return 'blue';
                    if (status === 'Not applicable') return 'grey';
                    return 'grey';
                  })()}>
                    {props?.data?.status}
                  </Badge>
                )}
              </SpaceBetween>
              <SpaceBetween direction="horizontal" size="xxxs">
                <Popover
                  dismissButton={false}
                  position="top"
                  size="small"
                  triggerType="custom"
                  content={props?.data?.stride_category}
                >
                  <Button
                    variant="icon"
                    ariaLabel="Stride category"
                    fullWidth
                    iconSvg={CategoryIcon()}
                  ></Button>
                </Popover>
                <Popover
                  dismissButton={false}
                  position="top"
                  size="large"
                  triggerType="custom"
                  content={
                    // Format impact for tooltip - skip first line (overall impact), show only CIA breakdown
                    <div style={{ whiteSpace: 'pre-wrap', maxWidth: '600px', minWidth: '400px' }}>
                      {(() => {
                        const lines = props?.data?.impact?.split('\n') || [];
                        if (lines.length > 1) {
                          // Skip first line (High/Medium/Low), show only CIA details with bold labels
                          return lines.slice(1).map((line, index) => {
                            // Make "High Confidentiality", "High Integrity", "Medium Availability" bold
                            const parts = line.split(' - ');
                            if (parts.length >= 2) {
                              return (
                                <div key={index}>
                                  <strong>{parts[0]}</strong> - {parts.slice(1).join(' - ')}
                                </div>
                              );
                            }
                            return <div key={index}>{line}</div>;
                          });
                        }
                        return props?.data?.impact;
                      })()}
                    </div>
                  }
                >
                  <Button
                    variant="icon"
                    ariaLabel="Impact"
                    fullWidth
                    iconSvg={ImpactIcon()}
                  ></Button>
                </Popover>
                <Popover
                  dismissButton={false}
                  position="top"
                  size="small"
                  triggerType="custom"
                  content={props?.data?.target}
                >
                  <Button
                    variant="icon"
                    ariaLabel="Target"
                    fullWidth
                    iconSvg={TargetIcon()}
                  ></Button>
                </Popover>
              </SpaceBetween>
            </SpaceBetween>
          </Header>
        }
      >
        {/* NEW: SME Comparison Alert */}
        {props?.smeComparisonFlag && (
          <Box margin={{ bottom: 's' }} padding="s" variant="div">
            <SpaceBetween size="xs">
              <Box variant="h4" color="text-status-warning">
                ⚠️ SME Comparison Alert
              </Box>
              <Box variant="p">
                <strong>AI Rating:</strong> {props.smeComparisonFlag.ai_rating} | 
                <strong> SME Rating:</strong> {props.smeComparisonFlag.sme_rating}
              </Box>
              <Box variant="p">
                <strong>Similar SME Threat:</strong> {props.smeComparisonFlag.similar_sme_threat}
                {props.smeComparisonFlag.sme_component && (
                  <> (Component: {props.smeComparisonFlag.sme_component})</>
                )}
              </Box>
              {props.smeComparisonFlag.sme_rationale && (
                <Box variant="p">
                  <strong>SME Rationale:</strong> {props.smeComparisonFlag.sme_rationale}
                </Box>
              )}
              <Box variant="p" color="text-body-secondary">
                <strong>Recommendation:</strong> {props.smeComparisonFlag.recommendation}
              </Box>
            </SpaceBetween>
          </Box>
        )}
        <Grid
          gridDefinition={[
            { colspan: { default: 12, xxs: 6 } },
            { colspan: { default: 12, xxs: 6 } },
            { colspan: { default: 12, xxs: 12 } },
          ]}
        >
          <div style={{ marginTop: 5 }}>{props?.data?.description}</div>
          <SpaceBetween>
            <Header variant="h3">Mitigations:</Header>
            <TokenGroup
              onDismiss={({ detail: { itemIndex } }) => {
                setItems([...items.slice(0, itemIndex), ...items.slice(itemIndex + 1)]);
              }}
              items={items}
              readOnly
            />
          </SpaceBetween>
          <ExpandableSection headerText="Details">
            <div>
              <Box variant="span">
                <SpaceBetween size="xs">
                  <div>
                    <strong>Likelihood:</strong> {props?.data?.likelihood}
                  </div>
                  <div>
                    <strong>Category:</strong> {props?.data?.stride_category}
                  </div>
                  <div>
                    <strong>Risk Score:</strong> {props?.data?.risk_score || 'Not calculated'}
                  </div>
                  <div>
                    <div>
                      {(() => {
                        const lines = props?.data?.impact?.split('\n') || [];
                        if (lines.length > 1) {
                          // Format as "Impact: High" on same line, then indented CIA breakdown
                          return (
                            <>
                              <strong>Impact:</strong> {lines[0]}
                              <div style={{ whiteSpace: 'pre-wrap', marginLeft: '20px' }}>
                                {lines.slice(1).map(line => `  ${line}`).join('\n')}
                              </div>
                            </>
                          );
                        }
                        return (
                          <>
                            <strong>Impact:</strong> {props?.data?.impact}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <div>
                    <strong>ISO 27001 Controls:</strong> {props?.data?.iso_controls || 'Not specified'}
                  </div>
                  <div>
                    <strong>Target:</strong> {props?.data?.target}
                  </div>
                  <div>
                    <strong>Threat source:</strong> {props?.data?.source}
                  </div>
                  <div>
                    <strong>Attack vector:</strong> {props?.data?.vector}
                  </div>
                  <div>
                    <strong>Prerequisites:</strong>{" "}
                    {props?.data?.prerequisites?.join(", ") || "None"}
                  </div>
                </SpaceBetween>
              </Box>
            </div>
          </ExpandableSection>
        </Grid>
      </Container>
      {!isReadOnly && (
        <ModalComponent
          headers={props.headers}
          data={action === "edit" ? props?.data : {}}
          visible={openModal}
          setVisible={setOpenModal}
          index={props?.index}
          updateData={props?.updateData}
          action={action}
          type={props?.type}
          hasColumn={true}
          columnConfig={{
            left: ["name", "description", "likelihood", "stride_category", "impact", "target", "status", "comments"],
            right: ["source", "vector", "prerequisites", "mitigations"],
          }}
        />
      )}
    </>
  );
});
